// @givmo/board — the first card on the page of a seat for someone who isn't on the board, in the
// "Director and titles" card's place (CBG-YR6-OUTSIDE, frame o3).
//
// Each committee the seat serves on, as the committee's page lists its members: the committee's name,
// opening its page, with the role and, where the organization recorded it so, that the member doesn't
// vote on it; the membership's start beside it. Closing the card, where a board seat's card says what
// everyone serving on the board gets, what this seat opens: its committees' meetings, papers, minutes
// and written consents, never the board's, and never a vote on the board or a place in its quorum.

import type { OrgBoardMemberOut } from '@givmo/api-client';
import { Card, CardBody } from '@givmo/design-system';
import { Link } from '../../board-routing';
import { displayDate } from '../../lib/display';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import { committeesWords as w } from '../committees/committees-words';

const PLAIN = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';

export function OutsideCommitteesCard({ seat }: { seat: Pick<OrgBoardMemberOut, 'committees'> }) {
  const committees = seat.committees ?? [];
  return (
    <Card testId="member-detail-committees-card">
      <CardBody className="flex flex-col gap-12">
        <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
          {co.committeesCard}
        </h2>
        {committees.length > 0 ? (
          <ul className="flex flex-col">
            {committees.map((row) => (
              <li
                key={row.committee_id}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-16 gap-y-2 border-t border-border-subtle py-10 first:border-t-0 first:pt-0 max-sm:grid-cols-1"
                data-testid={`member-detail-committee-${row.committee_id}`}
              >
                <span className="flex min-w-0 flex-col gap-2">
                  <Link
                    to="/committees/$committeeId"
                    params={{ committeeId: row.committee_id }}
                    className="font-sans text-s14 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline"
                  >
                    {row.name}
                  </Link>
                  <span className={PLAIN}>
                    {[
                      row.role === 'chair' ? w.roleChair : w.roleMember,
                      ...(row.votes === false ? [w.doesNotVote] : []),
                    ].join(' · ')}
                  </span>
                </span>
                {row.serves_from ? (
                  <span className={`${QUIET} tabular-nums`}>
                    {w.since(displayDate(row.serves_from))}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          // Not drawn: every membership has ended; the closing sentence would name no committee.
          <span className={PLAIN} data-testid="member-detail-committees-none">
            {co.seatOnNoCommittee}
          </span>
        )}
        {committees.length > 0 ? (
          <p
            className={`${QUIET} border-t border-border-subtle pt-12`}
            data-testid="member-detail-scope"
          >
            {co.scopeSentence}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
